import React, { useContext, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import qs from "query-string";
import cx from "classnames";

import Button from "Components/button/Button";
import FullPaginationControls from "Components/fullPaginationControls/FullPaginationControls";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import CustomerRow from "Components/CustomerRow/CustomerRow";
import ConfirmModal from "Components/confirmModal/ConfirmModal";
import TranslatedString from "Components/TranslatedString";
import Select from "Components/select/Select";
import DropdownIndicator from "Components/customArrowSelect/CustomArrowSelect";

import { plus } from "Icons/index";
import { MediaQueriesContext } from "../../App";
import { useApiQuery, useApiMutation } from "../../hooks";

import { t } from "i18next";

import s from "./listCustomers.scss";

const TABLE_HEADERS = (isMobileDevice) => [
  {
    name: t("table.header.status"),
    size: isMobileDevice ? "2rem" : "4rem",
    center: true,
  },
  { name: t("table.header.plan"), size: "3rem" },
  { name: t("table.header.type"), size: "4rem", hideOnMobile: true },
  { name: t("table.header.customer") },
  { name: t("table.header.ehawk"), size: "3.5rem" },
  { name: t("table.header.domains"), size: "4rem", hideOnMobile: true },
  { name: t("table.header.accounts.8gb"), size: "4.5rem", hideOnMobile: true },
  { name: t("table.header.accounts.25gb"), size: "3rem", hideOnMobile: true },
  { name: t("table.header.accounts.50gb"), size: "3rem", hideOnMobile: true },
  { name: t("table.header.accounts.100gb"), size: "3rem", hideOnMobile: true },
  { name: t("table.header.accounts.archive"), size: "3rem", hideOnMobile: true },
  { name: t("table.header.customization"), size: "3rem", hideOnMobile: true },
  { name: t("table.header.date.creation"), size: "6rem", hideOnMobile: true },
  { name: t("table.header.date.expiry"), size: "6rem", hideOnMobile: true },
  { size: "3rem", center: true },
];

export const CustomersListScreen = () => {
  const STATUS_OPTIONS = [
    { label: t("select.status.all"), value: "all" },
    { label: t("select.status.enabled"), value: "enabled" },
    { label: t("select.status.blocked"), value: "blocked" },
    { label: t("select.status.expired"), value: "expired" },
    { label: t("select.status.waiting"), value: "waiting" },
  ];

  const history = useHistory();
  const location = useLocation();
  const [modalState, setModalState] = useState({
    isOpen: false,
    selectedCustomer: {},
  });

  const {
    data: customers,
    isLoading: customersIsLoading,
    getData: getCustomers,
    isRefreshing: customersIsRefreshing,
    pagination: customersPagination,
    updateData: updateCustomersData,
  } = useApiQuery("getCustomers", {
    enablePagination: true,
    autoRefresh: {
      shouldRefresh: (data) => data.some(({ status }) => ["updating", "deleting"].includes(status)),
      interval: 4000,
    },
  });

  const { mutate: deleteCustomer } = useApiMutation("deleteCustomer", {
    optimistic: (code) =>
      updateCustomersData((prev) => prev.map((c) => (c.code === code ? { ...c, status: "deleting" } : c))),
  });

  const { mutate: changeCustomerStatus } = useApiMutation("changeCustomerStatus", {
    optimistic: (code) =>
      updateCustomersData((prev) => prev.map((c) => (c.code === code ? { ...c, status: "updating" } : c))),
  });

  const handleModalClose = () => {
    setModalState({ isOpen: false, selectedCustomer: {} });
  };

  const handleModalOpen = (customer) => {
    setModalState({ isOpen: true, selectedCustomer: customer });
  };

  const handleAddNewCustomer = () => {
    history.push("customers/add");
  };

  const handleDeleteCustomer = async () => {
    const { code } = modalState.selectedCustomer;
    try {
      await deleteCustomer(code);
    } catch (error) {
      console.error("Error deleting customer:", error);
    }
    handleModalClose();
  };

  const handleStatusChange = async (code, status) => {
    try {
      await changeCustomerStatus(code, status);
    } catch (error) {
      console.error("Error changing customer status:", error);
    }
  };

  const { isMobileDevice, isMediaMobile } = useContext(MediaQueriesContext);

  const query = location.query;

  console.log("query", JSON.stringify(STATUS_OPTIONS, null, 2));

  return (
    <div className={s.listCustomers}>
      <div className={s.headerList}>
        <div className={cx("sectionTitle", s.titleList)}>{t("customers.list.header.title")}</div>

        {!isMediaMobile && (
          <div className={cx("sectionDesc", s.descList)}>
            <TranslatedString>customers.list.header.dsc</TranslatedString>
          </div>
        )}

        <div className={s.actionsList}>
          <Button icon={plus} onClick={handleAddNewCustomer} onlyIcon={isMediaMobile}>
            button.add.customer
          </Button>
        </div>
      </div>

      <div className={s.contentList}>
        <div className={s.filters}>
          <Select
            className={s.statusFilter}
            components={{ DropdownIndicator }}
            name="status"
            placeholder="status"
            isSearchable={false}
            options={STATUS_OPTIONS}
            value={query.status || "all"}
            onChange={(value) => {
              history.push({
                pathname: location.pathname,
                search: qs.stringify({
                  ...query,
                  status: value.status === "all" ? null : value.status,
                  page: 1,
                  per: 25,
                }),
              });
            }}
          />
          <FullPaginationControls
            className={s.fullPaginationControls}
            paginationInfo={customersPagination}
            location={location}
            refreshList={() => getCustomers()}
            listFilterParam="plan"
            listFilters={["all", "paid", "trial", "free"]}
            isLoading={customersIsLoading}
          />
        </div>

        <div className={s.table}>
          <ResponsiveTable
            history={history}
            areRowsLoading={customersIsLoading && !customersIsRefreshing}
            location={location}
            headers={TABLE_HEADERS(isMobileDevice)}
            rows={customers.map((customer) => (
              <CustomerRow
                key={customer.code}
                customer={customer}
                location={location}
                changeCustomerStatus={handleStatusChange}
                deleteCustomer={handleModalOpen}
              />
            ))}
          />
        </div>
      </div>

      <ConfirmModal
        title={t("confirmationModal.title.delte.customer")}
        text={
          <TranslatedString subs={{ customerName: modalState.selectedCustomer.fullname }}>
            confirmationModal.text.delete.customer
          </TranslatedString>
        }
        textButton="button.delete"
        opened={modalState.isOpen}
        action={handleDeleteCustomer}
        handleClose={handleModalClose}
      />
    </div>
  );
};

export default CustomersListScreen;
